import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue';
import { addDoc, collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ArrowLeft, Building2, ExternalLink, FileText, Hash, Landmark, Mail, MapPin, Pencil, Phone, Plus, Search, X } from 'lucide-vue-next';
import { db } from '@/firebase';
const emptyForm = () => ({
    name: '', supplierCode: '', email: '', address: '', phone: '', vatNumber: '', bankAccount: '',
    logoUrl: '', canPlaceStockOrder: false, isActive: true
});
export default defineComponent({
    name: 'Suppliers',
    components: { ArrowLeft, Building2, ExternalLink, FileText, Hash, Landmark, Mail, MapPin, Pencil, Phone, Plus, Search, X },
    setup() {
        const suppliers = ref([]);
        const selectedSupplier = ref(null);
        const invoices = ref([]);
        const supplierItems = ref([]);
        const searchText = ref('');
        const loading = ref(true);
        const pageError = ref('');
        const formError = ref('');
        const formOpen = ref(false);
        const saving = ref(false);
        const editingSupplierId = ref(null);
        const form = ref(emptyForm());
        let suppliersUnsubscribe = null;
        let invoicesUnsubscribe = null;
        let itemsUnsubscribe = null;
        const filteredSuppliers = computed(() => {
            const queryText = searchText.value.trim().toLowerCase();
            return suppliers.value.filter(supplier => [supplier.name, supplier.supplierCode, supplier.email]
                .some(value => value.toLowerCase().includes(queryText)));
        });
        const formatMoney = (value) => {
            const amount = Number(value);
            if (!Number.isFinite(amount))
                return '—';
            return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 2 }).format(amount);
        };
        const formatDate = (value) => {
            if (!value)
                return '—';
            const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
            return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-ZA');
        };
        const stopRelatedListeners = () => {
            invoicesUnsubscribe?.();
            itemsUnsubscribe?.();
            invoicesUnsubscribe = null;
            itemsUnsubscribe = null;
            invoices.value = [];
            supplierItems.value = [];
        };
        const selectSupplier = (supplier) => {
            stopRelatedListeners();
            selectedSupplier.value = supplier;
            const supplierRef = doc(db, 'suppliers', supplier.id);
            invoicesUnsubscribe = onSnapshot(query(collection(supplierRef, 'invoices'), orderBy('invoiceDate', 'desc')), snapshot => {
                invoices.value = snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
            }, error => {
                console.error('Supplier invoices subscription failed:', error);
            });
            itemsUnsubscribe = onSnapshot(collection(supplierRef, 'items'), snapshot => {
                supplierItems.value = snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
            }, error => {
                console.error('Supplier items subscription failed:', error);
            });
        };
        const clearSelection = () => {
            stopRelatedListeners();
            selectedSupplier.value = null;
        };
        const openCreateForm = () => {
            editingSupplierId.value = null;
            form.value = emptyForm();
            formError.value = '';
            formOpen.value = true;
        };
        const openEditForm = (supplier) => {
            editingSupplierId.value = supplier.id;
            const { id, ...supplierFields } = supplier;
            form.value = { ...emptyForm(), ...supplierFields };
            formError.value = '';
            formOpen.value = true;
        };
        const closeForm = () => {
            if (!saving.value)
                formOpen.value = false;
        };
        const saveSupplier = async () => {
            saving.value = true;
            formError.value = '';
            const supplierData = {
                name: form.value.name,
                supplierCode: form.value.supplierCode,
                email: form.value.email,
                address: form.value.address,
                phone: form.value.phone,
                vatNumber: form.value.vatNumber,
                bankAccount: form.value.bankAccount,
                logoUrl: form.value.logoUrl,
                canPlaceStockOrder: form.value.canPlaceStockOrder,
                isActive: form.value.isActive,
                updatedAt: serverTimestamp()
            };
            try {
                if (editingSupplierId.value) {
                    await updateDoc(doc(db, 'suppliers', editingSupplierId.value), supplierData);
                }
                else {
                    const created = await addDoc(collection(db, 'suppliers'), {
                        ...supplierData,
                        createdAt: serverTimestamp()
                    });
                    selectedSupplier.value = { id: created.id, ...form.value };
                }
                formOpen.value = false;
            }
            catch (error) {
                console.error('Saving supplier failed:', error);
                formError.value = 'Could not save this supplier. Check Firestore permissions and try again.';
            }
            finally {
                saving.value = false;
            }
        };
        onMounted(() => {
            suppliersUnsubscribe = onSnapshot(query(collection(db, 'suppliers'), orderBy('name', 'asc')), snapshot => {
                suppliers.value = snapshot.docs.map(supplierDoc => {
                    const data = supplierDoc.data();
                    return {
                        id: supplierDoc.id,
                        name: String(data.name ?? ''),
                        supplierCode: String(data.supplierCode ?? ''),
                        email: String(data.email ?? ''),
                        address: String(data.address ?? ''),
                        phone: String(data.phone ?? ''),
                        vatNumber: String(data.vatNumber ?? ''),
                        bankAccount: String(data.bankAccount ?? ''),
                        logoUrl: String(data.logoUrl ?? ''),
                        canPlaceStockOrder: Boolean(data.canPlaceStockOrder),
                        isActive: data.isActive !== false
                    };
                });
                if (selectedSupplier.value) {
                    selectedSupplier.value = suppliers.value.find(supplier => supplier.id === selectedSupplier.value?.id) ?? null;
                    if (!selectedSupplier.value)
                        stopRelatedListeners();
                }
                loading.value = false;
            }, error => {
                console.error('Suppliers subscription failed:', error);
                pageError.value = 'Could not load suppliers. Check your connection and Firestore permissions.';
                loading.value = false;
            });
        });
        onUnmounted(() => {
            suppliersUnsubscribe?.();
            stopRelatedListeners();
        });
        return {
            suppliers, filteredSuppliers, selectedSupplier, invoices, supplierItems, searchText, loading,
            pageError, formError, formOpen, saving, editingSupplierId, form, formatMoney, formatDate,
            selectSupplier, clearSelection, openCreateForm, openEditForm, closeForm, saveSupplier
        };
    }
});
